import React, { useRef, useContext, useEffect, useCallback, useState, Fragment } from 'react'
import Image from 'next/image'

import Typography from 'Atoms/Typography'
import Panel from 'Atoms/Panel'
import Button from 'Molecules/Button'
import ReactGA from 'react-ga'
import Drawer from 'Molecules/Drawer'
import ErrorBoundary from 'UtilityComponents/ErrorBoundary'
import CoveringSpinner from 'Atoms/CoveringSpinner'
import { UserContext } from 'Contexts/UserContext/UserContext'
import { ListContext } from 'Contexts/ListContext/ListContext'

import ListCard, { ListCardSkeleton } from './listCard/listCard'
import ListCreationModal from 'Containers/ListCreationModal/ListCreationModal'

import styles from './listDrawer.module.scss'


const ListDrawer = () => {
  const { listContext, getUserLists, hideListDrawer, createNewList, addToList, addArticlesToList } = useContext(ListContext)
  const { context: userContext } = useContext(UserContext)
  const { itemsToAddToList, isListDrawerOpen } = listContext

  const [addingListId, setaddingListId] = useState(null)

  const isForProduct = listContext?.listDrawerType === 'product'

  let lists = listContext.lists

  const createListModal = useRef(null)
  const listCount = (lists?.filter(x => (!x.isSavedForLater || isForProduct)) || []).length

  useEffect(() => {
    if (isListDrawerOpen) {
      getUserLists(true)
    }
  }, [isListDrawerOpen])

  const AddToListWrapper = useCallback(async (listId, isSavedForLater, setLoading) => {

    if(setLoading) setLoading(true)
    
    ReactGA.event({
      category: 'BuildList',
      action: 'Add To List',
      label: isSavedForLater ? 'Added item to saved for later list' : `${isForProduct ? 'Product' : 'Article'}`,
    })

    handleMisoInteraction(itemsToAddToList, isForProduct)
    var addFunc = isForProduct ? addToList : addArticlesToList
    let success = await addFunc(isForProduct || typeof (itemsToAddToList[0]) === 'number' ? itemsToAddToList : itemsToAddToList.map(x => x.articleId), listId)
    if (success) {
      getUserLists(true)
      hideListDrawer()
    }
    if(setLoading) setLoading(false)

  }, [itemsToAddToList, addToList, listContext.listDrawerType])

  return (
    <>
      <Drawer
        portalId={'listDrawer'}
        location={'right'}
        width={640}
        open={isListDrawerOpen}
        closeFunction={() => hideListDrawer()}>
        <div className={styles.list_drawer} data-testid={`list_drawer_${isListDrawerOpen ? 'open' : 'closed'}`}>
          <div className={styles.list_drawer_body}>
            <Typography size={1} font={'bold'} className={styles.list_drawer_header}>
              Save this item to a List
            </Typography>
            {(itemsToAddToList || []).map(item =>
              <ErrorBoundary>
                <div className={styles.product}>
                  <Image
                    src={`https:${(((item.images && item.images.length) && item.images[0]) || '//content.speedwaymotors.com/OtherImages/missingimage2.jpg')}`}
                    width={item.articleId ? null : 48}
                    height={48}
                    className={item.title.length > 47 ? styles.product_image : null} />
                  <Typography size={0.875} font={'heading'} inline className={styles.product_title}>
                    {item.title}
                  </Typography>
                </div>
              </ErrorBoundary>)}
            <Panel className={styles.list_drawer_list_panel}>
              <Typography size={1.25}>
                Your Lists
              </Typography>
              <Typography size={0.875}>
                Save item to your lists {/*or save it to a vehicle in your garage */}
              </Typography>
              <Typography size={0.875}>
                {`${listCount} ${listCount == 1 ? 'List' : 'Lists'}`}
              </Typography>
              {lists ? (
                lists?.filter(x => (!x.isSavedForLater || isForProduct)).map((list, i) =>
                  <ListCard key={`list_${i}`}
                    listName={list.listName}
                    productCount={(list.productCount || 0)}
                    total={list.listPrice}
                    image={(list.products || []).length ? list.products[0].imageUrl : '//content.speedwaymotors.com/OtherImages/missingimage2.jpg'}
                    addToList={(setLoading) => AddToListWrapper(list.id, list.isSavedForLater, setLoading)}
                    listContainsItem={DoesListContainItem(list.products, list.articles, itemsToAddToList, listContext?.listDrawerType)} />
                )
              ) : (
                <>
                  <ListCardSkeleton />
                  <ListCardSkeleton />
                  <ListCardSkeleton />
                </>
              )}

            </Panel>
          </div>
          <Panel layer={0} className={styles.list_drawer_footer}>
            <Button
              intent={'action'}
              invert
              event={{
                category: null,
                action: 'Show Create New List Modal',
                label: 'List Drawer'
              }}
              onClick={() => {
                createListModal.current.open()
                hideListDrawer()
              }}
              text={'Create New List'}
              size={'small'}
              fill
              data-testid={'list_drawer_create_new_list'} />
          </Panel>
        </div>
      </Drawer>
      <ListCreationModal onCreateList={(listName) => createNewList(listName, isForProduct ? itemsToAddToList : null, !isForProduct ? itemsToAddToList.map(item => item.articleId) : null)} ref={createListModal} />
    </>
  )
}


function DoesListContainItem(products, articles, itemsToAddToList, type) {
  if (type === 'product' && (products || []).length) {
    return products.some(product =>
      itemsToAddToList?.some(item => product.sku == item.skuVariantNumber)
    )
  }

  if (type === 'article' && (articles || []).length) {
    return articles.some(article =>
      itemsToAddToList?.some(item => article.id == (typeof (item) == 'number' ? item : item.articleId))
    )
  }

  return false
}

async function handleMisoInteraction(itemsToAddToList, isForProduct) {
  if (window?.fireMisoInteraction) {
    if (isForProduct) {
      window.fireMisoInteraction({
        prod_id: itemsToAddToList[0].skuBaseNumber,
        sku: itemsToAddToList[0].skuVariantNumber,
        eventType: 'addToList'
      })
    }
    else {
      let articleId = typeof (itemsToAddToList[0]) == 'number' ? itemsToAddToList[0] : itemsToAddToList[0].articleId

      window.fireMisoInteraction({
        prod_id: `Article-${articleId}`,
        sku: `Article-${articleId}`,
        eventType: 'addToList'
      })
    }
  }
}

export default ListDrawer